// Label-format preview orchestrator (7.6.22 Slice 5). Fills a template body
// with synthetic sample data and dispatches to the driver-specific renderer.
//
// PHI-SAFE: `renderPreview` ONLY ever substitutes SAMPLE_FIELDS into the body
// — it does not accept live order/patient data. See sample-data.ts for the
// hard PHI rule this protects.

import type { PrinterDriver } from '@alpha-edge/shared'
import { SAMPLE_FIELDS } from './sample-data'
import { renderZplPreview, DEFAULT_DPI } from './labelary-client'
import { renderDymoPreview } from './dymo-preview'
import { inferZebraDimensions } from '../label-format'

// Same tag-matching regex the backend uses (`services/label-render.ts`
// `substituteVariables` / route-layer `{{...}}` matcher) so preview fill
// behaves identically to real rendering: unknown tags resolve to '' here
// (previews have no "missing" concept — a blank field is preferable to a
// literal `{{tag}}` showing up in a rendered image).
const TAG_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

function fillSampleFields(body: string): string {
  return body.replace(TAG_RE, (_match, name: string) =>
    Object.prototype.hasOwnProperty.call(SAMPLE_FIELDS, name) ? SAMPLE_FIELDS[name] : '',
  )
}

// Ports backend `injectDymoBarcode` (services/label-render.ts): DYMO barcode
// objects store a placeholder inside `<DataString>`/`<TextDataHolder><Value>`
// (mustache there breaks DYMO Connect's XML parser), so previews must inject
// the sample barcode directly into those elements the same way real
// rendering does, rather than relying on `{{tag}}` substitution.
function injectDymoBarcode(body: string, barcode: string): string {
  if (!barcode) return body
  let result = body.replace(/<DataString>[^<]*<\/DataString>/g, `<DataString>${barcode}</DataString>`)
  result = result.replace(/(<TextDataHolder>\s*<Value>)[^<]*(<\/Value>)/g, `$1${barcode}$2`)
  return result
}

export interface RenderPreviewArgs {
  readonly driver: PrinterDriver
  readonly body: string
  readonly paperSize?: string
  readonly widthIn?: number
  readonly heightIn?: number
  readonly dpi?: number
}

const DEFAULT_ZEBRA_WIDTH_IN = 2
const DEFAULT_ZEBRA_HEIGHT_IN = 1

// Matches the `zebra_<w>x<h>` paperSize convention (see
// utils/label-format.ts `formatZebraPaperSize`), e.g. `zebra_2.1x1.5`.
const PAPER_SIZE_RE = /^zebra_(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/

function dimensionsFromPaperSize(paperSize: string | undefined): { widthIn: number; heightIn: number } | null {
  if (!paperSize) return null
  const match = PAPER_SIZE_RE.exec(paperSize)
  if (!match) return null
  return { widthIn: Number(match[1]), heightIn: Number(match[2]) }
}

/**
 * Renders a label-template body to a previewable image URL. The body is
 * filled with SAMPLE_FIELDS ONLY — never live data — before it is handed to
 * a renderer (Labelary for Zebra, local DYMO Connect for DYMO).
 */
export async function renderPreview({
  driver,
  body,
  paperSize,
  widthIn,
  heightIn,
  dpi = DEFAULT_DPI,
}: RenderPreviewArgs): Promise<string> {
  if (driver === 'zebra_browser_print') {
    const filled = fillSampleFields(body)
    const fromPaperSize = dimensionsFromPaperSize(paperSize)
    const inferred = widthIn == null || heightIn == null ? inferZebraDimensions(body, dpi) : null
    return renderZplPreview({
      body: filled,
      widthIn: widthIn ?? fromPaperSize?.widthIn ?? inferred?.widthIn ?? DEFAULT_ZEBRA_WIDTH_IN,
      heightIn: heightIn ?? fromPaperSize?.heightIn ?? inferred?.heightIn ?? DEFAULT_ZEBRA_HEIGHT_IN,
      dpi,
    })
  }

  // dymo_client
  const filled = injectDymoBarcode(fillSampleFields(body), SAMPLE_FIELDS.barcode)
  return renderDymoPreview(filled)
}

export { SAMPLE_FIELDS, sampleSerial } from './sample-data'
export { DPI_OPTIONS, DEFAULT_DPI, dpiToDpmm, LabelaryError, LABELARY_BASE_URL } from './labelary-client'
export type { LabelaryDpi, LabelaryErrorCode } from './labelary-client'
export { DymoPreviewError } from './dymo-preview'
export type { DymoPreviewErrorCode } from './dymo-preview'
